// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { AppIcon, Table } from '@shared/ui-kit/components';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { type FC } from 'react';
import { useAdvisoryConditions } from './use-advisory-conditions';
import { Translation } from '@shared/ui-kit/providers';
import { useTranslation } from 'react-i18next';
import type {
  AdvisoryInstitution,
  InstitutionAccount,
} from '@shared/data-access/types/brokerage-types';

/** Institution with optional products (for display: advised or other). */
type InstitutionWithOptionalProducts = Pick<AdvisoryInstitution, 'id' | 'name'> & {
  products?: { accounts?: InstitutionAccount[] };
};

function renderInstitutionAccounts(
  institutions: InstitutionWithOptionalProducts[],
  id: string,
  title: string,
) {
  return (
    <div className="space-y-5" data-testid={`accounts-${id}`}>
      <div className="flex items-center space-x-3">
        <AppIcon
          icon="Building"
          className="h-6 w-6 text-gray-600"
          data-testid={`accounts-icon-${id}`}
        />
        <h4
          className="text-lg font-semibold text-gray-900"
          data-testid={`accounts-title-${id}`}
        >
          {title}
        </h4>
      </div>

      <div className="space-y-4">
        {institutions.map(inst => (
          <div
            key={inst.id}
            className="space-y-2"
            data-testid={`institution-${id}-${inst.id}`}
          >
            <p
              className="text-sm font-medium text-gray-700"
              data-testid={`institution-name-${id}-${inst.id}`}
            >
              {inst.name}
            </p>
            <div className="space-y-2 pl-2">
              {(inst.products?.accounts ?? []).map(
                (account: InstitutionAccount) => (
                  <div
                    key={account.iban}
                    className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4"
                    data-testid={`account-${id}-${inst.id}-${account.iban}`}
                  >
                    <p
                      className="text-base font-semibold capitalize text-gray-900"
                      data-testid={`account-name-${id}-${inst.id}-${account.iban}`}
                    >
                      {account.name ?? account.iban}
                    </p>
                  </div>
                ),
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export const AdvisoryConditions: FC = () => {
  const { t } = useTranslation();
  const data = useAdvisoryConditions();

  return (
    <div className="mt-4 space-y-6" data-testid="advisory-conditions-container">
      <p
        className="border-b border-border-sep pb-4 text-sm font-normal text-subtitle"
        data-testid="advisory-conditions-service-provider"
      >
        <Translation
          i18nKey="client-profile.advisory-conditions.service-provider"
          ns="components"
        />
      </p>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="space-y-5">
          <div className="flex items-center space-x-3">
            <AppIcon icon="TrendingUp" className="h-6 w-6 text-finance-600" />
            <h4
              className="text-lg font-semibold text-finance-800"
              data-testid="advisory-type-title"
            >
              <Translation
                i18nKey="client-profile.advisory-conditions.advisory-type-title"
                ns="components"
              />
            </h4>
          </div>

          <div
            className={classNames(
              'rounded-lg border p-5',
              data.isActiveClient
                ? 'border-finance-200 bg-finance-50'
                : 'border-red-200 bg-red-50',
            )}
            data-testid="advisory-type-container"
          >
            <div className="flex items-center justify-between">
              <p
                className={classNames(
                  'text-base font-medium',
                  data.isActiveClient ? 'text-finance-800' : 'text-red-800',
                )}
                data-testid="advisory-type"
              >
                {data.advisoryData.type.description}
              </p>

              {!data.isActiveClient && (
                <AppIcon
                  icon="Info"
                  className="h-5 w-5 flex-shrink-0 text-negative"
                  data-testid="info-icon"
                />
              )}
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <div className="flex items-center space-x-3">
            <AppIcon icon="Percent" className="h-6 w-6 text-positive" />

            <h4
              className="text-lg font-semibold text-finance-800"
              data-testid="agreed-commission-title"
            >
              <Translation
                i18nKey="client-profile.advisory-conditions.agreed-commission"
                ns="components"
              />
            </h4>
          </div>

          <div
            className={classNames(
              'rounded-lg border p-5 text-center',
              data.isActiveClient
                ? 'border-green-200 bg-green-50'
                : 'border-finance-200 bg-gray-50',
            )}
            data-testid="agreed-commission-container"
          >
            <p
              className={classNames(
                'mb-2 text-3xl font-bold',
                data.isActiveClient ? 'text-positive' : 'text-finance-700',
              )}
              data-testid="agreed-commission"
            >
              <Table.Data
                format="percentage"
                value={data.advisoryData.commission}
                formatOptions={{
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }}
              />
            </p>

            <p
              className={classNames(
                'text-base',
                data.isActiveClient ? 'text-green-700' : 'text-finance-600',
              )}
              data-testid="agreed-commission-info"
            >
              <Translation
                i18nKey="client-profile.advisory-conditions.commission-info"
                ns="components"
              />
            </p>
          </div>
        </div>
      </div>

      {data.advisoryData.advisedInstitutions.length > 0 &&
        renderInstitutionAccounts(
          data.advisoryData.advisedInstitutions,
          'connected',
          data.isActiveClient
            ? t('client-profile.advisory-conditions.advised-accounts', {
                ns: 'components',
              })
            : t('client-profile.advisory-conditions.connected-accounts', {
                ns: 'components',
              }),
        )}
    </div>
  );
};
